/**
 * QSignalTable Component (v11.0) - Ultra-Compact Professional Grid
 * Expert-level polished UI with minimal wasted space
 *
 * v11.0 UI/UX Polish:
 * - Ultra-compact category tabs (4px 8px padding, 4px gap)
 * - Tighter column widths (reduced by ~30%)
 * - Standardized fonts (11px headers, 12px symbols, 11px values)
 * - Reduced row height (8px padding vs 10px)
 * - Mobile-first responsive (min-width 580px from 760px)
 * - Touch targets maintained (44px min for mobile)
 *
 * @version 11.0.0
 */

import React, { useState, memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Collapse, Tooltip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import ShowChartIcon from '@mui/icons-material/ShowChart';

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - Compact & Professional
// ═══════════════════════════════════════════════════════════════════════════

const THEME = {
  // Backgrounds
  bg: '#0D0D12',
  bgCard: '#111116',
  bgHeader: '#0A0A0F',
  bgRow: 'transparent',
  bgRowAlt: 'rgba(255,255,255,0.02)',
  bgHover: 'rgba(255,255,255,0.05)',

  // Borders
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.04)',

  // Text - High contrast
  textPrimary: '#FFFFFF',
  textSecondary: '#B0B8C4',
  textMuted: '#6B7280',

  // Signal Colors
  buy: '#10B981',
  buyBg: 'rgba(16, 185, 129, 0.15)',
  buyBorder: 'rgba(16, 185, 129, 0.3)',
  sell: '#EF4444',
  sellBg: 'rgba(239, 68, 68, 0.15)',
  sellBorder: 'rgba(239, 68, 68, 0.3)',
  neutral: '#6B7280',
  neutralBg: 'rgba(107, 114, 128, 0.15)',
  neutralBorder: 'rgba(107, 114, 128, 0.3)',

  // Technical Indicators
  pivot: '#8B5CF6',
  fib: '#F59E0B',
  ma: '#3B82F6',
};

// ═══════════════════════════════════════════════════════════════════════════
// SPACING CONSTANTS - Ultra Compact with Mobile-First Responsive
// ═══════════════════════════════════════════════════════════════════════════

const SPACING = {
  // Table - Responsive (PHASE 3.1: Strict height enforcement)
  headerHeight: { xs: '32px', sm: '36px' },  // PHASE 3.1: 36px header on desktop
  rowHeight: { xs: '44px', sm: '32px' },     // PHASE 3.1: 32px rows on desktop, 44px mobile (WCAG touch)
  headerPadding: { xs: '4px 8px', sm: '4px 8px' },  // PHASE 3.1: 8px horizontal
  rowPadding: { xs: '4px 8px', sm: '4px 8px' },     // PHASE 3.1: 8px horizontal
  cellGap: '2px',

  // Category tabs - Enhanced for readability & touch targets
  tabPadding: { xs: '5px 10px', sm: '6px 12px' },
  tabGap: '4px',

  // Fonts (Expert Typography Scale v13 - Responsive)
  headerFont: { xs: '0.625rem', sm: '0.6875rem' },   // 10px mobile, 11px desktop
  symbolFont: { xs: '0.75rem', sm: '0.8125rem' },    // 12px mobile, 13px desktop
  valueFont: { xs: '0.6875rem', sm: '0.75rem' },     // 11px mobile, 12px desktop
  badgeFont: { xs: '0.625rem', sm: '0.6875rem' },    // 10px mobile, 11px desktop
  tabFont: { xs: '0.8125rem', sm: '0.875rem' },      // 13px mobile, 14px desktop
  tabCountFont: { xs: '0.6875rem', sm: '0.75rem' },  // 11px mobile, 12px desktop
  footerFont: { xs: '0.625rem', sm: '0.6875rem' },   // 10px mobile, 11px desktop
};

// Font family - consistent with chat window
const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Column widths - Responsive grid layouts
// Desktop: Symbol | Signal | % | Pivot | R1 | S1 | F38 | F50 | F62 | expand
// Mobile: Symbol | Signal | % | Pivot | R1 | S1 (hide F38, F50, F62 for space)
const GRID_COLUMNS = {
  xs: 'minmax(55px, 1.2fr) minmax(42px, 0.8fr) 28px minmax(44px, 1fr) minmax(44px, 1fr) minmax(44px, 1fr) 18px',
  sm: 'minmax(65px, 1.2fr) minmax(48px, 0.8fr) 30px minmax(48px, 1fr) minmax(48px, 1fr) minmax(48px, 1fr) minmax(44px, 1fr) minmax(44px, 1fr) minmax(44px, 1fr) 20px',
  md: 'minmax(70px, 1.2fr) minmax(50px, 0.8fr) 32px minmax(52px, 1fr) minmax(52px, 1fr) minmax(52px, 1fr) minmax(48px, 1fr) minmax(48px, 1fr) minmax(48px, 1fr) 20px'
};
const GRID_MIN_WIDTH = { xs: '320px', sm: '480px', md: '520px' };

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORIZATION
// ═══════════════════════════════════════════════════════════════════════════

const categorizeSymbol = (symbol) => {
  if (!symbol) return 'forex';
  const upper = symbol.toUpperCase().trim();
  if (upper.includes('/')) return 'pairs';
  if (upper.includes('XAU') || upper.includes('XAG') || upper === 'GOLD' || upper === 'SILVER') return 'metals';
  if (upper.includes('US30') || upper.includes('US500') || upper.includes('NAS') || upper.includes('DAX')) return 'indices';
  // FIX: Add crypto category (was missing - BTCUSD/ETHUSD were incorrectly categorized as forex)
  if (upper.includes('BTC') || upper.includes('ETH') || upper.includes('SOL')) return 'crypto';
  return 'forex';
};

const ALL_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'forex', label: 'Forex' },
  { id: 'metals', label: 'Metals' },
  { id: 'crypto', label: 'Crypto' },  // FIX: Added crypto category (was missing)
  { id: 'indices', label: 'Indices' },
  { id: 'pairs', label: 'Pairs' },
];

// ═══════════════════════════════════════════════════════════════════════════
// SIGNAL LOGIC
// ═══════════════════════════════════════════════════════════════════════════

const getSignalVerdict = (signal) => {
  const { qSignal, qScore, zSignal, zScore, trendScore } = signal;
  const score = qScore ?? trendScore ?? 50;

  if (qSignal) {
    const upper = qSignal.toUpperCase();
    if (upper === 'HOLD' || upper === 'WAIT' || upper === 'NEUTRAL') {
      return { action: 'NEUTRAL', type: 'neutral', score };
    }
    if (upper.includes('BUY')) return { action: 'BULLISH', type: 'buy', score };
    if (upper.includes('SELL')) return { action: 'BEARISH', type: 'sell', score };
  }

  if (score <= 60) return { action: 'NEUTRAL', type: 'neutral', score };

  let direction = 'neutral';
  if (zSignal) {
    const upper = zSignal.toUpperCase();
    if (upper.includes('BUY')) direction = 'buy';
    else if (upper.includes('SELL')) direction = 'sell';
  } else if (zScore !== undefined) {
    direction = zScore < 0 ? 'buy' : zScore > 0 ? 'sell' : 'neutral';
  }

  if (direction === 'buy') return { action: 'BULLISH', type: 'buy', score };
  if (direction === 'sell') return { action: 'BEARISH', type: 'sell', score };
  return { action: 'NEUTRAL', type: 'neutral', score };
};

const getTypeColor = (type) => {
  if (type === 'buy') return THEME.buy;
  if (type === 'sell') return THEME.sell;
  return THEME.neutral;
};

const getTypeBg = (type) => {
  if (type === 'buy') return THEME.buyBg;
  if (type === 'sell') return THEME.sellBg;
  return THEME.neutralBg;
};

const getTypeBorder = (type) => {
  if (type === 'buy') return THEME.buyBorder;
  if (type === 'sell') return THEME.sellBorder;
  return THEME.neutralBorder;
};

const formatPrice = (value) => {
  if (value === null || value === undefined) return '—';
  const abs = Math.abs(value);
  if (abs >= 1000) return value.toFixed(1);
  if (abs >= 100) return value.toFixed(2);
  if (abs >= 1) return value.toFixed(3);
  return value.toFixed(4);
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const QSignalTable = memo(({ signals = [], onAskQ }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expandedRows, setExpandedRows] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [hiddenSymbols, setHiddenSymbols] = useState(() => {
    try {
      const saved = localStorage.getItem('qai_hidden_symbols');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const toggleSymbolVisibility = useCallback((symbol) => {
    setHiddenSymbols(prev => {
      const newHidden = prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol];
      localStorage.setItem('qai_hidden_symbols', JSON.stringify(newHidden));
      return newHidden;
    });
  }, []);

  const categoryCounts = useMemo(() => {
    // FIX: Added crypto: 0 (was missing - counts[crypto]++ would fail)
    const counts = { all: 0, pairs: 0, forex: 0, metals: 0, crypto: 0, indices: 0 };
    signals.forEach(s => {
      if (!hiddenSymbols.includes(s.symbol)) {
        counts.all++;
        const cat = categorizeSymbol(s.symbol);
        if (counts[cat] !== undefined) {
          counts[cat]++;
        }
      }
    });
    return counts;
  }, [signals, hiddenSymbols]);

  const filteredSignals = useMemo(() => {
    let filtered = signals.filter(s => !hiddenSymbols.includes(s.symbol));
    if (activeCategory !== 'all') {
      filtered = filtered.filter(s => categorizeSymbol(s.symbol) === activeCategory);
    }
    return filtered;
  }, [signals, activeCategory, hiddenSymbols]);

  const toggleRow = useCallback((symbol) => {
    setExpandedRows(prev => ({ ...prev, [symbol]: !prev[symbol] }));
  }, []);

  if (!signals || signals.length === 0) {
    return (
      <Box sx={{ backgroundColor: THEME.bgCard, borderRadius: '6px', p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: THEME.textMuted, fontSize: '13px' }}>
          Waiting for data...
        </Typography>
      </Box>
    );
  }

  // Get responsive values based on breakpoint
  const gridColumns = isMobile ? GRID_COLUMNS.xs : GRID_COLUMNS.md;
  const gridMinWidth = isMobile ? GRID_MIN_WIDTH.xs : GRID_MIN_WIDTH.md;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '4px', sm: '6px' }, height: '100%' }}>
      {/* CATEGORY TABS - Mobile-optimized grid layout */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: '4px', sm: '6px' },
        flexWrap: { xs: 'wrap', sm: 'nowrap' }
      }}>
        <Box sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'none' },
          gap: '4px',
          flexWrap: 'wrap',
          flex: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          {ALL_CATEGORIES.map((cat) => (
            <CategoryTab
              key={cat.id}
              label={cat.label}
              count={categoryCounts[cat.id]}
              isActive={activeCategory === cat.id}
              onClick={() => setActiveCategory(cat.id)}
            />
          ))}
        </Box>

        <Tooltip title="Manage Symbols" arrow>
          <IconButton
            onClick={() => setShowSettings(!showSettings)}
            size="small"
            sx={{
              width: { xs: 36, sm: 28 },
              height: { xs: 36, sm: 28 },
              minWidth: { xs: 44, sm: 28 },  // WCAG touch target on mobile
              minHeight: { xs: 44, sm: 28 },
              backgroundColor: showSettings ? THEME.buyBg : 'transparent',
              border: `1px solid ${showSettings ? THEME.buy : THEME.border}`,
              borderRadius: '4px',
              color: showSettings ? THEME.buy : THEME.textMuted,
              '&:hover': { backgroundColor: THEME.bgHover, borderColor: THEME.buy },
            }}
          >
            <TuneIcon sx={{ fontSize: { xs: '16px', sm: '14px' } }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* SETTINGS PANEL */}
      <Collapse in={showSettings}>
        <SymbolSettings signals={signals} hiddenSymbols={hiddenSymbols} onToggle={toggleSymbolVisibility} onClose={() => setShowSettings(false)} />
      </Collapse>

      {/* SIGNALS TABLE - Mobile-First Responsive */}
      <Box sx={{
        backgroundColor: THEME.bgCard,
        borderRadius: { xs: '4px', sm: '6px' },
        border: `1px solid ${THEME.border}`,
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Horizontal scroll wrapper with visible indicator on mobile */}
        <Box sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',  // Smooth momentum scrolling on iOS
          '&::-webkit-scrollbar': { height: { xs: '4px', sm: '3px' } },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: { xs: 'rgba(255,255,255,0.2)', sm: 'rgba(255,255,255,0.1)' },
            borderRadius: '2px'
          },
        }}>
          {/* Table Header - PHASE 3.1: 36px height */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: SPACING.cellGap,
            padding: SPACING.headerPadding,
            height: SPACING.headerHeight,  // PHASE 3.1: Explicit 36px header
            backgroundColor: THEME.bgHeader,
            borderBottom: `1px solid ${THEME.border}`,
            alignItems: 'center',
            minWidth: gridMinWidth,
          }}>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Symbol
            </Typography>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
              Trend
            </Typography>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
              %
            </Typography>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.pivot, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
              Pivot
            </Typography>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.sell, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
              R1
            </Typography>
            <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.buy, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
              S1
            </Typography>
            {/* Fibonacci columns - hidden on mobile (xs) */}
            {!isMobile && (
              <>
                <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.fib, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                  F38
                </Typography>
                <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.fib, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                  F50
                </Typography>
                <Typography sx={{ fontSize: SPACING.headerFont, fontWeight: 600, color: THEME.fib, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                  F62
                </Typography>
              </>
            )}
            <Box />
          </Box>

          {/* Table Body */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '3px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.1)', borderRadius: '2px' },
          }}>
            {filteredSignals.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: THEME.textMuted, fontSize: '12px' }}>No data in this category</Typography>
              </Box>
            ) : (
              filteredSignals.map((signal, idx) => (
                <SignalRow
                  key={signal.symbol}
                  signal={signal}
                  isEven={idx % 2 === 0}
                  expanded={expandedRows[signal.symbol] || false}
                  onToggle={() => toggleRow(signal.symbol)}
                  onAskQ={onAskQ}
                  isMobile={isMobile}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{
          padding: { xs: '4px 6px', sm: '5px 8px' },
          borderTop: `1px solid ${THEME.border}`,
          backgroundColor: THEME.bgHeader,
        }}>
          <Typography sx={{ fontSize: SPACING.footerFont, color: THEME.textMuted }}>
            {filteredSignals.length} item{filteredSignals.length !== 1 ? 's' : ''}
            {hiddenSymbols.length > 0 && <span style={{ color: THEME.sell, marginLeft: '6px' }}>• {hiddenSymbols.length} hidden</span>}
            {isMobile && <span style={{ color: THEME.textMuted, marginLeft: '6px', opacity: 0.7 }}>• Swipe for more</span>}
          </Typography>
        </Box>
      </Box>

      {/* MOVING AVERAGES GRID */}
      <TechnicalsPanel signals={filteredSignals} />
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORY TAB - Ultra Compact
// ═══════════════════════════════════════════════════════════════════════════

const CategoryTab = memo(({ label, count, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: { xs: 'center', sm: 'flex-start' },
      gap: SPACING.tabGap,
      padding: SPACING.tabPadding,
      borderRadius: { xs: '4px', sm: '6px' },
      cursor: 'pointer',
      backgroundColor: isActive ? THEME.bgCard : 'transparent',
      border: `1px solid ${isActive ? THEME.border : 'transparent'}`,
      transition: 'all 0.15s ease',
      minHeight: { xs: '36px', sm: '32px' },  // WCAG touch target
      minWidth: { xs: 'auto', sm: 'auto' },
      '&:hover': { backgroundColor: THEME.bgHover },
      // Better tap feedback on mobile
      '&:active': { transform: 'scale(0.98)', backgroundColor: THEME.bgHover },
    }}
  >
    <Typography
      sx={{
        fontSize: SPACING.tabFont,
        fontWeight: isActive ? 600 : 500,
        fontFamily: FONT_FAMILY,
        color: isActive ? THEME.textPrimary : THEME.textMuted,
        letterSpacing: '0.01em',
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
    <Box sx={{
      fontSize: SPACING.tabCountFont,
      fontWeight: 700,
      fontFamily: FONT_FAMILY,
      color: isActive ? THEME.buy : THEME.textMuted,
      backgroundColor: isActive ? THEME.buyBg : 'rgba(255,255,255,0.08)',
      padding: { xs: '1px 5px', sm: '2px 6px' },
      borderRadius: '4px',
      minWidth: { xs: '16px', sm: '18px' },
      textAlign: 'center',
      lineHeight: 1.3,
    }}>
      {count}
    </Box>
  </Box>
));

// ═══════════════════════════════════════════════════════════════════════════
// SIGNAL ROW - Ultra Compact
// ═══════════════════════════════════════════════════════════════════════════

const SignalRow = memo(({ signal, isEven, expanded, onToggle, onAskQ, isMobile }) => {
  const verdict = getSignalVerdict(signal);

  // Extract technical data
  const pivot = signal.pivot?.pp || signal.distribution?.p50;
  const r1 = signal.pivot?.r1 || signal.distribution?.p75;
  const s1 = signal.pivot?.s1 || signal.distribution?.p25;
  const fib38 = signal.fibLevels?.fib382;
  const fib50 = signal.fibLevels?.fib500;
  const fib62 = signal.fibLevels?.fib618;

  // Use responsive grid values
  const gridColumns = isMobile ? GRID_COLUMNS.xs : GRID_COLUMNS.md;
  const gridMinWidth = isMobile ? GRID_MIN_WIDTH.xs : GRID_MIN_WIDTH.md;

  return (
    <Box>
      {/* Main Row - PHASE 3.1: 32px data rows */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: SPACING.cellGap,
          padding: SPACING.rowPadding,
          alignItems: 'center',
          backgroundColor: isEven ? THEME.bgRow : THEME.bgRowAlt,
          borderBottom: `1px solid ${THEME.borderLight}`,
          cursor: 'pointer',
          transition: 'background-color 0.1s ease',
          minWidth: gridMinWidth,
          height: SPACING.rowHeight,  // PHASE 3.1: Explicit 32px row height (44px mobile for WCAG)
          '&:hover': { backgroundColor: THEME.bgHover },
          '&:active': isMobile ? { backgroundColor: THEME.bgHover } : {},
        }}
      >
        {/* Symbol */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Box sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            backgroundColor: getTypeColor(verdict.type),
            boxShadow: `0 0 4px ${getTypeColor(verdict.type)}`,
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontSize: SPACING.symbolFont,
            fontWeight: 600,
            color: THEME.textPrimary,
            fontFamily: "'Inter', sans-serif",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {signal.symbol}
          </Typography>
        </Box>

        {/* Signal Badge */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px 6px',
          borderRadius: '3px',
          backgroundColor: getTypeBg(verdict.type),
          border: `1px solid ${getTypeBorder(verdict.type)}`,
        }}>
          <Typography sx={{
            fontSize: SPACING.badgeFont,
            fontWeight: 700,
            color: getTypeColor(verdict.type),
            letterSpacing: '0.02em',
          }}>
            {verdict.action}
          </Typography>
        </Box>

        {/* Score */}
        <Typography sx={{
          fontSize: SPACING.valueFont,
          fontWeight: 600,
          color: getTypeColor(verdict.type),
          fontFamily: "'JetBrains Mono', monospace",
          textAlign: 'center',
        }}>
          {verdict.score}
        </Typography>

        {/* Pivot */}
        <Typography sx={{
          fontSize: SPACING.valueFont,
          fontWeight: 500,
          color: pivot ? THEME.textSecondary : THEME.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          textAlign: 'right',
        }}>
          {formatPrice(pivot)}
        </Typography>

        {/* R1 */}
        <Typography sx={{
          fontSize: SPACING.valueFont,
          fontWeight: 500,
          color: r1 ? THEME.sell : THEME.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          textAlign: 'right',
        }}>
          {formatPrice(r1)}
        </Typography>

        {/* S1 */}
        <Typography sx={{
          fontSize: SPACING.valueFont,
          fontWeight: 500,
          color: s1 ? THEME.buy : THEME.textMuted,
          fontFamily: "'JetBrains Mono', monospace",
          textAlign: 'right',
        }}>
          {formatPrice(s1)}
        </Typography>

        {/* Fibonacci columns - hidden on mobile to save space */}
        {!isMobile && (
          <>
            {/* Fib 38% */}
            <Typography sx={{
              fontSize: SPACING.valueFont,
              fontWeight: 500,
              color: fib38 ? THEME.fib : THEME.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              textAlign: 'right',
            }}>
              {formatPrice(fib38)}
            </Typography>

            {/* Fib 50% */}
            <Typography sx={{
              fontSize: SPACING.valueFont,
              fontWeight: 500,
              color: fib50 ? THEME.fib : THEME.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              textAlign: 'right',
            }}>
              {formatPrice(fib50)}
            </Typography>

            {/* Fib 62% */}
            <Typography sx={{
              fontSize: SPACING.valueFont,
              fontWeight: 500,
              color: fib62 ? THEME.fib : THEME.textMuted,
              fontFamily: "'JetBrains Mono', monospace",
              textAlign: 'right',
            }}>
              {formatPrice(fib62)}
            </Typography>
          </>
        )}

        {/* Expand Arrow */}
        <Box sx={{ color: THEME.textMuted, display: 'flex', justifyContent: 'center' }}>
          {expanded ? (
            <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
          ) : (
            <KeyboardArrowRightIcon sx={{ fontSize: '16px' }} />
          )}
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded} timeout={150}>
        <ExpandedDetails signal={signal} verdict={verdict} onAskQ={onAskQ} />
      </Collapse>
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// EXPANDED DETAILS - Compact
// ═══════════════════════════════════════════════════════════════════════════

const ExpandedDetails = memo(({ signal, verdict, onAskQ }) => {
  const hasMA = signal.ema?.ema20 || signal.ema?.ema50 || signal.ema?.ema200;
  const r2 = signal.pivot?.r2;
  const s2 = signal.pivot?.s2;

  return (
    <Box sx={{
      padding: '10px 12px',
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderBottom: `1px solid ${THEME.border}`,
    }}>
      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: hasMA || signal.zScore !== undefined ? '8px' : 0 }}>  {/* PHASE 1.2: 8px section gap */}
        {/* Extended Levels */}
        {(r2 || s2) && (
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: '6px' }}>
              Extended
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {r2 && <LevelChip label="R2" value={formatPrice(r2)} color={THEME.sell} />}
              {s2 && <LevelChip label="S2" value={formatPrice(s2)} color={THEME.buy} />}
            </Box>
          </Box>
        )}

        {/* Moving Averages */}
        {hasMA && (
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: '6px' }}>
              MAs
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {signal.ema?.ema20 && <LevelChip label="20" value={formatPrice(signal.ema.ema20)} color={THEME.ma} />}
              {signal.ema?.ema50 && <LevelChip label="50" value={formatPrice(signal.ema.ema50)} color={THEME.ma} />}
              {signal.ema?.ema200 && <LevelChip label="200" value={formatPrice(signal.ema.ema200)} color={THEME.ma} />}
            </Box>
          </Box>
        )}

        {/* Z-Score */}
        {signal.zScore !== undefined && (
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: '6px' }}>
              Z-Score
            </Typography>
            <Typography sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: signal.zScore > 0 ? THEME.sell : THEME.buy,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {signal.zScore.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Ask Q Button */}
      {onAskQ && (
        <Box
          onClick={() => onAskQ(signal)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '6px 10px',
            backgroundColor: THEME.buyBg,
            border: `1px solid ${THEME.buyBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.12s ease',
            minHeight: '32px', // Touch target
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.25)',
            },
          }}
        >
          <ShowChartIcon sx={{ fontSize: '14px', color: THEME.buy }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.buy }}>
            Ask Q about {signal.symbol}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

// Level Chip - Ultra Compact
const LevelChip = memo(({ label, value, color }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px',
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: '4px',
    border: `1px solid ${THEME.borderLight}`,
  }}>
    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: THEME.textMuted }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: '11px',
      fontWeight: 600,
      color,
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {value}
    </Typography>
  </Box>
));

// ═══════════════════════════════════════════════════════════════════════════
// TECHNICALS PANEL - Compact
// ═══════════════════════════════════════════════════════════════════════════

const TechnicalsPanel = memo(({ signals }) => {
  const signalsWithMA = signals.filter(s =>
    s.ema?.ema20 || s.ema?.ema50 || s.ema?.ema200
  );

  if (signalsWithMA.length === 0) return null;

  return (
    <Box sx={{
      backgroundColor: THEME.bgCard,
      borderRadius: '6px',
      border: `1px solid ${THEME.border}`,
      overflow: 'hidden',
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        backgroundColor: THEME.bgHeader,
        borderBottom: `1px solid ${THEME.border}`,
      }}>
        <ShowChartIcon sx={{ fontSize: '14px', color: THEME.ma }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textPrimary }}>
          Moving Averages
        </Typography>
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, ml: 'auto' }}>
          {signalsWithMA.length} symbols
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        {/* Header - PHASE 3.1: 36px height */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '80px repeat(3, 70px)',
          gap: '2px',
          padding: '4px 8px',  // PHASE 3.1: 8px horizontal
          height: '36px',  // PHASE 3.1: Explicit header height
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderBottom: `1px solid ${THEME.borderLight}`,
          minWidth: '300px',
        }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Symbol</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.ma, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>MA 20</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.ma, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>MA 50</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.ma, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>MA 200</Typography>
        </Box>

        {/* Rows - PHASE 3.1: 32px height */}
        <Box sx={{ maxHeight: '140px', overflowY: 'auto' }}>
          {signalsWithMA.slice(0, 10).map((signal, idx) => (
            <Box
              key={signal.symbol}
              sx={{
                display: 'grid',
                gridTemplateColumns: '80px repeat(3, 70px)',
                gap: '2px',
                padding: '4px 8px',  // PHASE 3.1: 8px horizontal
                height: '32px',  // PHASE 3.1: Explicit row height
                alignItems: 'center',
                backgroundColor: idx % 2 === 0 ? 'transparent' : THEME.bgRowAlt,
                minWidth: '300px',
                '&:hover': { backgroundColor: THEME.bgHover },
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: THEME.textPrimary }}>{signal.symbol}</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: THEME.ma, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.01em' }}>
                {formatPrice(signal.ema?.ema20)}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: THEME.ma, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.01em' }}>
                {formatPrice(signal.ema?.ema50)}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: THEME.ma, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.01em' }}>
                {formatPrice(signal.ema?.ema200)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// SYMBOL SETTINGS - Compact
// ═══════════════════════════════════════════════════════════════════════════

const SymbolSettings = memo(({ signals, hiddenSymbols, onToggle, onClose }) => {
  const grouped = useMemo(() => {
    const groups = { forex: [], metals: [], indices: [], pairs: [] };
    signals.forEach(s => groups[categorizeSymbol(s.symbol)].push(s.symbol));
    return groups;
  }, [signals]);

  return (
    <Box sx={{
      backgroundColor: THEME.bgCard,
      border: `1px solid ${THEME.border}`,
      borderRadius: '6px',
      padding: '10px',
      mb: '4px',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '10px' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME.textPrimary }}>Manage Symbols</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: THEME.textMuted, width: 24, height: 24, '&:hover': { color: THEME.textPrimary } }}>
          <CloseIcon sx={{ fontSize: '14px' }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {Object.entries(grouped).map(([cat, symbols]) => symbols.length > 0 && (
          <Box key={cat}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: THEME.textMuted, mb: '6px', textTransform: 'uppercase' }}>
              {cat} ({symbols.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {symbols.map(sym => (
                <Box
                  key={sym}
                  onClick={() => onToggle(sym)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: hiddenSymbols.includes(sym) ? 'transparent' : THEME.bgHover,
                    border: `1px solid ${hiddenSymbols.includes(sym) ? THEME.border : THEME.buyBorder}`,
                    opacity: hiddenSymbols.includes(sym) ? 0.5 : 1,
                    transition: 'all 0.12s ease',
                    minHeight: '28px', // Touch target
                    '&:hover': { opacity: 1, backgroundColor: THEME.bgHover },
                  }}
                >
                  {hiddenSymbols.includes(sym) ? (
                    <VisibilityOffIcon sx={{ fontSize: '12px', color: THEME.sell }} />
                  ) : (
                    <VisibilityIcon sx={{ fontSize: '12px', color: THEME.buy }} />
                  )}
                  <Typography sx={{ fontSize: '11px', fontWeight: 500, color: THEME.textPrimary }}>{sym}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// PROP TYPES & DISPLAY NAMES
// ═══════════════════════════════════════════════════════════════════════════

QSignalTable.displayName = 'QSignalTable';
CategoryTab.displayName = 'CategoryTab';
SignalRow.displayName = 'SignalRow';
ExpandedDetails.displayName = 'ExpandedDetails';
LevelChip.displayName = 'LevelChip';
TechnicalsPanel.displayName = 'TechnicalsPanel';
SymbolSettings.displayName = 'SymbolSettings';

QSignalTable.propTypes = {
  signals: PropTypes.array,
  onAskQ: PropTypes.func,
};

CategoryTab.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

SignalRow.propTypes = {
  signal: PropTypes.object.isRequired,
  isEven: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onAskQ: PropTypes.func,
  isMobile: PropTypes.bool,
};

ExpandedDetails.propTypes = {
  signal: PropTypes.object.isRequired,
  verdict: PropTypes.object.isRequired,
  onAskQ: PropTypes.func,
};

LevelChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

TechnicalsPanel.propTypes = {
  signals: PropTypes.array.isRequired,
};

SymbolSettings.propTypes = {
  signals: PropTypes.array.isRequired,
  hiddenSymbols: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QSignalTable;
